// Report a browser-side error to the API journal (POST /client-log), so
// a white page on somebody's machine can be read on the server rather
// than described over the phone. Best effort, throttled, never throws.
import { url, tokenRef } from "api";

// TWO BUDGETS, AND IT SAYS WHEN IT STARTS DROPPING.
//
// One budget of 10/min covered everything, and a single click now produces
// five reports (the picker, onClickChildInList, row-found, the click chain,
// the scroll). Two clicks spent the minute, and every report after that was
// dropped in silence - which is why 2026-10-21's clicks produced no
// model-view/scroll line at all and looked like code that never ran. A trace
// is deliberate and bounded, so it gets its own budget; an error stays on the
// small one so a crash loop still cannot flood the log. When either budget
// runs out the reporter says so once per window, because a dropped report has
// to be distinguishable from a step that did not happen.
let sent = 0;
let sentTrace = 0;
let windowStart = 0;
let saidThrottled = false;
const MAX_PER_MINUTE = 10;
const MAX_TRACE_PER_MINUTE = 60;

function send(body) {
  const headers = { "Content-Type": "application/json" };
  try { const t = localStorage.getItem(tokenRef); if (t) headers.Authorization = `Bearer ${t}`; } catch (x) { /* no token */ }
  fetch(`${url}/client-log`, { method: "POST", headers, body: JSON.stringify(body), keepalive: true }).catch(() => {});
}

// The throttle notice: not subject to the budget it is reporting on.
function post(where, err) {
  try {
    send({
      where: String(where),
      page: typeof window !== "undefined" ? window.location.href : "",
      user: "",
      message: String((err && err.message) || "").slice(0, 400),
      stack: "",
    });
  } catch (x) { /* never let the reporter be the error */ }
}

export function reportClientError(where, err, extra = {}) {
  try {
    const now = Date.now();
    if (now - windowStart > 60000) { windowStart = now; sent = 0; sentTrace = 0; saidThrottled = false; }
    // INSTRUMENTATION IS NOT AN ERROR, WHATEVER IT IS CALLED.
    //
    // 2026-10-21, from the owner's own log:
    //   {"droppedFrom":"model-view/scroll","sent":10,"sentTrace":7}
    // The throttle notice worked exactly as built - and what it dropped was a
    // scroll measurement, because only names beginning "trace/" got the
    // instrumentation budget and these diagnostics are named for the page
    // they come from. So the small error budget, meant to stop a crash loop
    // flooding the log, was being spent on the evidence instead. Classify by
    // PURPOSE, not by the prefix someone happened to choose.
    const INSTRUMENT = ["trace/", "model-view/", "reporter/"];
    const isTrace = INSTRUMENT.some((p) => String(where || "").startsWith(p));
    const overBudget = isTrace ? sentTrace >= MAX_TRACE_PER_MINUTE : sent >= MAX_PER_MINUTE;
    if (overBudget) {
      if (saidThrottled) return;
      saidThrottled = true;
      // Say it once, through the same pipe, so the gap in the log is labelled.
      post("reporter/throttled", { message: JSON.stringify({ droppedFrom: String(where || "").slice(0, 60), sent, sentTrace }) });
      return;
    }
    if (isTrace) sentTrace += 1; else sent += 1;
    const e = err || {};
    const body = {
      where: String(where || "window"),
      page: typeof window !== "undefined" ? window.location.href : "",
      user: (() => { try { return localStorage.getItem("email") || localStorage.getItem("userEmail") || ""; } catch (x) { return ""; } })(),
      // 400 IS AN ERROR MESSAGE'S LENGTH, NOT A TRACE'S. This was the THIRD
      // place clipping the same payload - the helper that sends it, the trace
      // that builds it and the route that receives it - and fixing the other
      // two left this one cutting every chain off at exactly 400 characters.
      // One rule, in one place: the server caps a trace at 8000 regardless.
      message: String(e.message || e.reason || e || "").slice(0, isTrace ? 8000 : 400),
      stack: String((e.stack) || (e.reason && e.reason.stack) || extra.componentStack || "").slice(0, 900),
    };
    send(body);
  } catch (x) { /* never let the reporter be the error */ }
}

// Wire once, for errors nothing caught: thrown outside React's tree,
// promise rejections, and chunk loads that failed after a deploy.
let wired = false;
export function wireGlobalErrorReporting() {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("error", (ev) => {
    const err = ev && (ev.error || { message: ev.message, stack: `${ev.filename}:${ev.lineno}:${ev.colno}` });
    reportClientError("window.onerror", err);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    reportClientError("unhandledrejection", ev && ev.reason ? ev.reason : { message: "unhandled rejection" });
  });
}
